import { Object as FabricObject, Canvas } from 'fabric';

import { Position } from '../../types/metaBullets';
import { useCanvasStore } from '../../store/canvas';
import { generateObjectId } from '../../utils/generateId';
import { isBulletType, TextEntityType } from '../../types/fabric-extensions';

import { TextEntity } from './TextEntity';
import { Marker } from './Marker';

/**
 * Класс MetaBullet - виртуальная группа TextEntity объектов
 * НЕ является Fabric.js объектом, а только управляет группой TextEntity
 */
export class MetaBullet {
  // Уникальный ID метабуллита
  id: string;

  // ID canvas к которому принадлежит метабуллит
  canvasId: string;

  // Массив ID сущностей в порядке их отображения
  entityIds: string[] = [];

  // Якорная позиция первой сущности (сохраняется при первом расчете)
  // Используется для предотвращения смещения метабулита при драге внутренних сущностей
  private anchorTop: number | null = null;
  private anchorLeft: number | null = null; // Якорь для X координаты

  // Конструктор
  constructor(canvasId: string, initialEntityId: string, predefinedId?: string) {
    this.id = predefinedId || generateObjectId(canvasId);
    this.canvasId = canvasId;
    this.entityIds = [initialEntityId];
  }

  /**
   * Получить все TextEntity сущности метабуллита с canvas
   */
  getEntities(): TextEntity[] {
    const canvas = useCanvasStore.getState().getFabricInstance(this.canvasId);
    if (!canvas) {
      console.warn('⚠️ Canvas not found for MetaBullet:', this.canvasId);
      return [];
    }

    const entities = this.entityIds
      .map((id) => {
        const obj = canvas.getObjects().find((fabricObj: FabricObject) => fabricObj.id === id);
        return obj && TextEntity.isTextEntity(obj) ? obj : null;
      })
      .filter((obj): obj is TextEntity => obj !== null);

    // НЕ сортируем по entityOrder, используем порядок из entityIds
    // Массив entityIds уже содержит правильный порядок после splice/push

    return entities;
  }

  /**
   * Получить все маркеры связанные с сущностями этого метабуллита
   */
  getMarkers(): Marker[] {
    const entities = this.getEntities();
    const markers: Marker[] = [];

    entities.forEach((entity) => {
      const marker = entity.getMarker();
      if (marker) {
        markers.push(marker);
      }
    });

    return markers;
  }

  /**
   * Добавить новую сущность в метабуллит
   * @param entity - TextEntity объект
   * @param insertIndex - Позиция вставки (по умолчанию в конец)
   * @param skipRecalculation - Пропустить автоматическую рекалькуляцию (для случаев когда маркер будет добавлен позже)
   */
  addEntity(entity: TextEntity, insertIndex?: number, skipRecalculation = false): void {
    // Устанавливаем связь с метабуллитом
    entity.metaBulletId = this.id;

    // Добавляем в нужную позицию
    if (insertIndex !== undefined && insertIndex >= 0 && insertIndex <= this.entityIds.length) {
      this.entityIds.splice(insertIndex, 0, entity.id);
    } else {
      this.entityIds.push(entity.id);
    }

    // Пересчитываем порядок сущностей
    this.reorderEntities();

    // Пересчитываем позиции (если не указано пропустить)
    if (!skipRecalculation) {
      this.recalculatePositions(true);
    }
  }

  /**
   * Удалить сущность из метабуллита
   * @param entityId - ID сущности для удаления
   */
  removeEntity(entityId: string): void {
    const index = this.entityIds.indexOf(entityId);
    if (index > -1) {
      this.entityIds.splice(index, 1);

      // Если удалена первая сущность, сбрасываем якоря
      if (index === 0) {
        this.anchorTop = null;
        this.anchorLeft = null;
      }

      if (this.entityIds.length !== 0) {
        // Пересчитываем порядок и позиции
        this.reorderEntities();
        this.recalculatePositions(true);
      }
    }
  }

  /**
   * Сбросить якорные позиции (вызывается при явном перемещении первой сущности)
   */
  resetAnchor(): void {
    console.debug('🔄 [resetAnchor] Resetting anchors, old values:', {
      anchorLeft: this.anchorLeft,
      anchorTop: this.anchorTop,
    });
    this.anchorTop = null;
    this.anchorLeft = null;
  }

  /**
   * Пересчитать позиции всех сущностей в метабуллите
   * @param animated - Использовать анимацию при перемещении
   */
  async recalculatePositions(animated: boolean = true): Promise<void> {
    const allEntities = this.getEntities();

    // 🔢 ИСКЛЮЧАЕМ digital-accent-superscript из автоматического позиционирования
    // Они позиционируются относительно своих digit сущностей
    const entities = allEntities.filter((entity) => {
      if (entity.entityType === 'digital-accent-superscript') {
        return false;
      }
      return true;
    });

    // 🎯 КРИТИЧНО: Пропускаем если система в режиме ActiveSelection
    if (entities.length > 0 && entities[0].canvas) {
      const metaBulletSystem = (
        entities[0].canvas as Canvas & { _metaBulletSystem?: { isInActiveSelectionMode: boolean } }
      )._metaBulletSystem;
      if (metaBulletSystem && metaBulletSystem.isInActiveSelectionMode) {
        return;
      }
    }

    if (entities.length === 0) {
      return;
    }

    // Сначала рассчитываем целевые позиции для всех сущностей
    const targetPositions: Array<{ entity: TextEntity; targetX: number; targetY: number }> = [];

    // ==================================================================================
    // ЯКОРНАЯ ПОЗИЦИЯ ПЕРВОЙ СУЩНОСТИ
    // ==================================================================================
    // Используем сохраненную якорную позицию первой сущности, чтобы предотвратить
    // смещение всего метабулита при драге внутренних сущностей или изменении порядка.
    // Якорь сохраняется при первом успешном расчете позиций.
    //
    // ВАЖНО: Якорь — это позиция ТЕКСТА первой сущности, а не минимальный верхний край.
    // Это критично для корректной работы с маркерами, которые могут быть выше текста.
    // ==================================================================================
    let currentY: number;

    if (this.anchorTop === null) {
      // Первый расчет - используем текущую позицию ТЕКСТА первой сущности
      currentY = entities[0].top || 0;
      // Сохраняем как якорь для будущих пересчетов
      this.anchorTop = currentY;
    } else {
      // Используем сохраненную якорную позицию
      currentY = this.anchorTop;
    }

    // ==================================================================================
    // ЯКОРЬ ДЛЯ X: БАЗОВАЯ ЛЕВАЯ ПОЗИЦИЯ МЕТАБУЛЛИТА
    // ==================================================================================
    // Сохраняем РЕАЛЬНУЮ левую границу первой сущности для стабильности метабуллита
    // Это критично для предотвращения "уезжания" метабуллита при:
    // - Перетаскивании внутренних сущностей
    // - Изменении порядка сущностей
    // - Смене типа сущности (Tab/Shift+Tab)
    // ==================================================================================
    const firstEntity = entities[0];
    const firstEntityMarkerInfo = this.getEntityMarkerInfo(firstEntity);

    let baseX: number;

    if (this.anchorLeft === null) {
      // Первый расчет - вычисляем baseX из текущей позиции первой сущности
      if (firstEntityMarkerInfo.hasMarker && firstEntityMarkerInfo.markerLeft !== null) {
        // Если у первой сущности есть маркер, baseX = РЕАЛЬНАЯ позиция маркера
        baseX = firstEntityMarkerInfo.markerLeft;
      } else {
        // Если маркера нет, baseX = РЕАЛЬНАЯ позиция текста первой сущности
        baseX = firstEntity.left || 0;
      }
      // Сохраняем как якорь для будущих пересчетов
      this.anchorLeft = baseX;
    } else {
      // Используем сохраненную якорную позицию
      baseX = this.anchorLeft;
    }

    // Рассчитываем целевые позиции для всех сущностей
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];

      // Добавляем отступ сверху для всех сущностей, кроме первой
      const spacingTop = i === 0 ? 0 : this.calculateSpacing(entity, 'top');

      // ==================================================================================
      // РАСЧЕТ ПОЗИЦИИ ТЕКСТА С УЧЕТОМ МАРКЕРА
      // ==================================================================================
      // Проблема: если маркер выше текста (например, offsetY: 'center' и маркер больше текста),
      // то spacingTop должен применяться к верхнему краю маркера, а не к тексту.
      //
      // Алгоритм:
      // 1. Предполагаем, что текст будет на позиции currentY + spacingTop
      // 2. Рассчитываем, где будет верхний край маркера для этой позиции
      // 3. Если маркер выше текста:
      //    - Верхний край маркера должен быть на currentY + spacingTop
      //    - Рассчитываем позицию текста на основе этого
      // 4. Если маркер не выше текста:
      //    - Применяем spacingTop к тексту как обычно
      //
      // ИСКЛЮЧЕНИЕ: Для первой сущности (i === 0) используем currentY (якорь) как есть,
      // без пересчетов. Это критично для предотвращения смещения при сбросе ActiveSelection.
      // ==================================================================================
      let targetY: number;

      if (i === 0) {
        // Для первой сущности используем якорь напрямую
        // Это предотвращает смещение при восстановлении после ActiveSelection
        targetY = currentY;
      } else {
        // Для остальных сущностей применяем логику с учетом маркера
        const tentativeTextTop = currentY + spacingTop;
        const { minTop: markerMinTopForTentative } = this.getMarkerMinTop(entity, tentativeTextTop);

        if (markerMinTopForTentative < tentativeTextTop) {
          // Маркер выше текста: применяем spacingTop к верхнему краю маркера
          // Верхний край маркера должен быть на currentY + spacingTop
          const markerTopWithSpacing = currentY + spacingTop;
          // Рассчитываем позицию текста на основе верхнего края маркера
          const { entityTop } = this.getEntityTopFromMarkerTop(entity, markerTopWithSpacing);
          targetY = entityTop;
        } else {
          // Маркер не выше текста: применяем spacingTop к тексту как обычно
          targetY = currentY + spacingTop;
        }
      }

      // ==================================================================================
      // ✅ НОВЫЙ АЛГОРИТМ РАСЧЕТА targetX (согласно Prompt.txt)
      // ==================================================================================
      // Правила:
      // 1. heading - всегда на baseX (без отступа)
      // 2. bullet-level-N (любой уровень):
      //    - Ищем вверх первую подходящую сущность (того же уровня, heading, или меньший уровень)
      //    - Левая граница МАРКЕРА текущего буллита = левая граница ТЕКСТА найденной сущности
      // 3. Первая сущность (i === 0) всегда использует baseX для стабильности (предотвращение дергания)
      // ==================================================================================

      const isBullet = isBulletType(entity.entityType);
      const isHeading = entity.entityType.startsWith('heading');
      const markerInfo = this.getEntityMarkerInfo(entity);
      const isFirstEntity = i === 0;

      let targetX: number;

      if (isFirstEntity) {
        // ==================================================================================
        // ПЕРВАЯ СУЩНОСТЬ: Используем baseX для стабильности (как якорь для Y)
        // ==================================================================================
        // Это критично для предотвращения дергания метабуллита при драге внутренних сущностей
        if (markerInfo.hasMarker) {
          const markerWidth = markerInfo.markerWidth || 0;
          const markerSpacing = this.getMarkerSpacing(entity);
          targetX = baseX + markerWidth + markerSpacing;
        } else {
          targetX = baseX;
        }
      } else if (isHeading) {
        // ==================================================================================
        // HEADING: Всегда на baseX (без отступа)
        // ==================================================================================
        if (markerInfo.hasMarker) {
          const markerWidth = markerInfo.markerWidth || 0;
          const markerSpacing = this.getMarkerSpacing(entity);
          targetX = baseX + markerWidth + markerSpacing;
        } else {
          targetX = baseX;
        }
      } else if (isBullet) {
        // ==================================================================================
        // BULLET: Ищем референсную сущность и выравниваем маркер
        // ==================================================================================
        const referenceEntity = this.findReferenceEntityForBullet(entity, entities);

        if (referenceEntity) {
          // Нашли референсную сущность - получаем позицию для выравнивания
          // (это может быть позиция текста или позиция маркера в зависимости от типа референса)
          const alignmentPosition = this.getReferenceAlignmentPosition(
            entity,
            referenceEntity,
            targetPositions,
          );

          if (alignmentPosition !== null) {
            if (markerInfo.hasMarker) {
              const markerWidth = markerInfo.markerWidth || 0;
              const markerSpacing = this.getMarkerSpacing(entity);

              // Проверяем: это позиция маркера или текста?
              const isReferenceBullet = isBulletType(referenceEntity.entityType);
              const currentLevel = this.extractBulletLevel(entity.entityType);
              const referenceLevel = this.extractBulletLevel(referenceEntity.entityType);
              const isSameLevel = currentLevel === referenceLevel;

              if (isReferenceBullet && isSameLevel) {
                // Bullet того же уровня: alignmentPosition = позиция маркера референса
                // Ставим наш маркер на ту же позицию, текст смещается на markerWidth + spacing
                targetX = alignmentPosition + markerWidth + markerSpacing;
              } else {
                // Heading или bullet меньшего уровня: alignmentPosition = позиция текста референса
                // Ставим наш маркер так, чтобы текст начинался на alignmentPosition
                targetX = alignmentPosition + markerWidth + markerSpacing;
              }
            } else {
              // Нет маркера - текст выравнивается по позиции выравнивания
              targetX = alignmentPosition;
            }
          } else {
            // Fallback: референс найден, но позиция не рассчитана (не должно случиться)
            targetX = baseX;
          }
        } else {
          // Не нашли референс - используем baseX (первый буллит в метабуллите)
          if (markerInfo.hasMarker) {
            const markerWidth = markerInfo.markerWidth || 0;
            const markerSpacing = this.getMarkerSpacing(entity);
            targetX = baseX + markerWidth + markerSpacing;
          } else {
            targetX = baseX;
          }
        }
      } else {
        // ==================================================================================
        // ДРУГИЕ ТИПЫ: Используем baseX
        // ==================================================================================
        if (markerInfo.hasMarker) {
          const markerWidth = markerInfo.markerWidth || 0;
          const markerSpacing = this.getMarkerSpacing(entity);
          targetX = baseX + markerWidth + markerSpacing;
        } else {
          targetX = baseX;
        }
      }

      targetPositions.push({
        entity,
        targetX,
        targetY,
      });

      // ==================================================================================
      // РАСЧЕТ ПОЗИЦИИ СЛЕДУЮЩЕЙ СУЩНОСТИ
      // ==================================================================================
      // Используем targetY вместо entity.top, потому что координаты маркера еще не обновлены
      // Получаем эффективную высоту (включая маркер) и минимальный верхний край
      //
      // ВАЖНО: Для последней сущности не добавляем spacingBottom (после нее нет сущностей)
      const isLastEntity = i === entities.length - 1;
      const { effectiveHeight, minTop } = this.getEffectiveEntityHeight(
        entity,
        targetY,
        isLastEntity,
      );

      // Обновляем currentY для следующей сущности
      // ВАЖНО: используем targetY (а не старый currentY), так как targetY уже включает spacingTop!
      //
      // Если маркер выше текста (minTop < targetY), то следующая сущность должна начинаться
      // от верхнего края маркера + effectiveHeight
      if (minTop < targetY) {
        currentY = minTop + effectiveHeight;
      } else {
        // Иначе: от позиции текста + effectiveHeight
        // Используем targetY (а не currentY!), потому что targetY = currentY + spacingTop
        currentY = targetY + effectiveHeight;
      }
    }

    // Теперь анимируем все сущности ПАРАЛЛЕЛЬНО
    if (animated) {
      const animationPromises = targetPositions.map(({ entity, targetX, targetY }) => {
        // Проверяем нужна ли анимация
        if (Math.abs(entity.top! - targetY) > 1 || Math.abs(entity.left! - targetX) > 1) {
          return this.animateEntityToPosition(entity, { x: targetX, y: targetY });
        } else {
          // Если анимация не нужна, просто устанавливаем позицию
          entity.set({ left: targetX, top: targetY });
          entity.setCoords();
          // Обновляем позицию маркера
          entity.updateMarkerPosition?.();
          return Promise.resolve();
        }
      });

      // Ждем завершения ВСЕХ анимаций одновременно
      await Promise.all(animationPromises);
    } else {
      // Без анимации - просто устанавливаем позиции
      targetPositions.forEach(({ entity, targetX, targetY }) => {
        entity.set({ left: targetX, top: targetY });
        entity.setCoords();
        // Обновляем позицию маркера
        entity.updateMarkerPosition?.();
      });
    }

    // Обновляем canvas после всех изменений
    const canvas = useCanvasStore.getState().getFabricInstance(this.canvasId);
    if (canvas) {
      canvas.requestRenderAll();

      // Восстанавливаем возможность создания бекапов для всех сущностей
      entities.forEach((entity) => entity.enableBackup());

      // 🔢 ОБНОВЛЯЕМ ПОЗИЦИИ DIGITAL-ACCENT SUPERSCRIPT
      // После основного позиционирования обновляем позиции связанных superscript
      entities.forEach((entity) => {
        if (entity.entityType === 'digital-accent' && entity.updateLinkedAccentPosition) {
          entity.updateLinkedAccentPosition();
        }
      });

      // Проверяем есть ли система MetaBulletSystem и в процессе ли инициализации
      // Если есть кастомное свойство _metaBulletSystem на canvas, используем его
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const system = canvas._metaBulletSystem;
      const backupManager = system?.getBackupManager();

      if (system && system.isInInitialization) {
        // Во время инициализации только отмечаем что были изменения
        system.markPositionChanges();
      } else if (backupManager) {
        // Проверяем, есть ли новые сущности (созданные через Enter, но еще не заполненные)
        // Если есть новые сущности - НЕ создаем бекап (бекап будет создан при выходе из редактирования)
        const hasNewEntities = entities.some(
          (entity) => (entity as unknown as { _isNewlyCreated?: boolean })._isNewlyCreated,
        );
        if (!hasNewEntities) {
          //  Используем новую систему BackupManager
          backupManager.startOperation('recalculation');
          backupManager.requestBackup();
          backupManager.finishOperation();
        }
      } else {
        // Fallback к старой логике если BackupManager не доступен
        // Проверяем новые сущности и здесь
        const hasNewEntities = entities.some(
          (entity) => (entity as unknown as { _isNewlyCreated?: boolean })._isNewlyCreated,
        );
        if (!hasNewEntities) {
          canvas.fire('metaBullet:backup:needed', {
            metaBulletId: this.id,
            entitiesCount: entities.length,
            entityIds: this.entityIds,
            timestamp: Date.now(),
          });
        }
      }

      // Обновляем hover подсветку если она показывается для этого MetaBullet
      if (system && system.currentHoveredMetaBulletId === this.id && system.hoverHighlightRect) {
        system.showHoverHighlight(this.id);
      }
    }
  }

  /**
   * Пересчитать порядок сущностей (entityOrder) в метабуллите
   */
  private reorderEntities(): void {
    const entities = this.getEntities();

    entities.forEach((entity, index) => {
      entity.entityOrder = index;
    });
  }

  /**
   * Анимировать перемещение сущности в новую позицию
   * @param entity - TextEntity для анимации
   * @param position - Целевая позиция
   */
  private async animateEntityToPosition(entity: TextEntity, position: Position): Promise<void> {
    return new Promise((resolve) => {
      // Анимируем только если есть реальное изменение позиции
      const needsAnimation =
        Math.abs(entity.left! - position.x) > 1 || Math.abs(entity.top! - position.y) > 1;

      if (!needsAnimation) {
        resolve();
        return;
      }

      // Используем нативную анимацию Fabric.js (исправляем easing тип)
      entity.animate(
        {
          top: position.y,
          left: position.x,
        },
        {
          duration: 300,
          // Используем функцию easing из fabric utils
          easing: (t: number, b: number, c: number, d: number) => {
            // easeOutCubic formula
            return c * ((t = t / d - 1) * t * t + 1) + b;
          },
          onChange: () => {
            const canvas = useCanvasStore.getState().getFabricInstance(this.canvasId);
            if (canvas) {
              // Обновляем позицию маркера во время анимации
              entity.updateMarkerPosition?.();
              canvas.requestRenderAll();
            }
          },
          onComplete: () => {
            entity.setCoords();
            // Финальное обновление позиции маркера после анимации
            entity.updateMarkerPosition?.();

            resolve();
          },
        },
      );
    });
  }

  /**
   * Рассчитать отступ сущности по коэффициенту
   * Формула: fontSize * коэффициент (коэффициент от бэкенда уже включает lineHeight)
   * @param entity - Текстовая сущность
   * @param type - Тип отступа ('top' или 'bottom')
   */
  private calculateSpacing(entity: TextEntity, type: 'top' | 'bottom'): number {
    const stylesConfig = entity.getStylesConfig();
    const entityTypeConfig = stylesConfig?.[entity.entityType];

    if (!entityTypeConfig) {
      // Fallback на старую систему если нет конфигурации
      return type === 'bottom' ? entity.marginBottom || 0 : 0;
    }

    // Специальная обработка для digital-accent
    if (entity.entityType === 'digital-accent' && 'digit' in entityTypeConfig) {
      const digitConfig = entityTypeConfig.digit;
      const fontSize = digitConfig.fontSize || entity.fontSize || 16;
      const coefficient =
        type === 'top' ? (digitConfig.spacingTop ?? 0) : (digitConfig.spacingBottom ?? 0);

      // Если коэффициент равен 0, возвращаем 0 (не используем marginBottom для обратной совместимости)
      if (coefficient === 0) {
        return 0;
      }

      // Коэффициент от бэкенда уже включает lineHeight, поэтому умножаем только на fontSize
      return fontSize * coefficient;
    }

    // Для остальных типов (включая digital-accent-superscript)
    if ('fontSize' in entityTypeConfig) {
      const fontSize = entityTypeConfig.fontSize || entity.fontSize || 16;
      const coefficient =
        type === 'top' ? (entityTypeConfig.spacingTop ?? 0) : (entityTypeConfig.spacingBottom ?? 0);

      // Если коэффициент равен 0, возвращаем 0 (не используем marginBottom для обратной совместимости)
      // Это позволяет явно установить нулевой отступ через spacingBottom: 0
      if (coefficient === 0) {
        return 0;
      }

      // Коэффициент от бэкенда уже включает lineHeight, поэтому умножаем только на fontSize
      return fontSize * coefficient;
    }

    // Fallback
    return type === 'bottom' ? entity.marginBottom || 0 : 0;
  }

  /**
   * Получить информацию о маркере сущности
   * Возвращает наличие маркера, его позицию и размеры
   * ИСПРАВЛЕНО: Учитывает систему координат маркера (center/center или left/top)
   */
  private getEntityMarkerInfo(entity: TextEntity): {
    hasMarker: boolean;
    markerLeft: number | null;
    markerWidth: number | null;
    markerHeight: number | null;
  } {
    const canvas = useCanvasStore.getState().getFabricInstance(this.canvasId);
    if (!canvas) {
      return { hasMarker: false, markerLeft: null, markerWidth: null, markerHeight: null };
    }

    const allObjects = canvas.getObjects();
    const linkedMarkers = allObjects.filter((obj: FabricObject) => {
      const marker = obj as unknown as Marker;
      return obj.type === 'marker' && !!marker.isMarker && marker.entityId === entity.id;
    }) as Marker[];

    if (linkedMarkers.length === 0) {
      return { hasMarker: false, markerLeft: null, markerWidth: null, markerHeight: null };
    }

    // Берем первый маркер (обычно он один)
    const marker = linkedMarkers[0];
    const markerWidth = marker.width || 0;
    const markerHeight = marker.height || 0;

    // КРИТИЧЕСКИ ВАЖНО: Конвертируем координату маркера в ЛЕВЫЙ КРАЙ
    // Бэкенд передает маркеры с center/center системой координат
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const markerOriginX = (marker as any).originX || 'center';
    let markerLeftEdge = marker.left || 0;

    if (markerOriginX === 'center') {
      // Если маркер с center origin - конвертируем в левый край
      markerLeftEdge = markerLeftEdge - markerWidth / 2;
    } else if (markerOriginX === 'right') {
      // Если маркер с right origin - конвертируем в левый край
      markerLeftEdge = markerLeftEdge - markerWidth;
    }
    // Если left - оставляем как есть

    return {
      hasMarker: true,
      markerLeft: markerLeftEdge,
      markerWidth: markerWidth,
      markerHeight: markerHeight,
    };
  }

  /**
   * Получить отступ между маркером и текстом из конфига
   */
  private getMarkerSpacing(entity: TextEntity): number {
    // Проверяем есть ли РЕАЛЬНЫЙ маркер на канвасе
    const hasRealMarker = entity.hasMarker?.() || !!entity.markerId;
    if (!hasRealMarker) {
      return 0;
    }

    const stylesConfig = entity.getStylesConfig();
    const entityTypeConfig = stylesConfig?.[entity.entityType];

    if (!entityTypeConfig || !('marker' in entityTypeConfig)) {
      return 0;
    }

    const markerConfig = entityTypeConfig.marker;
    if (!markerConfig || !markerConfig.enabled || !markerConfig.position) {
      return 0;
    }

    // offsetX теперь используется как отступ между маркером и текстом
    return markerConfig.position.offsetX || 0;
  }

  /**
   * Извлечь номер уровня из bullet-level-N
   */
  private extractBulletLevel(entityType: TextEntityType): number | null {
    const match = entityType.match(/^bullet-level-(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * ✅ НОВЫЙ АЛГОРИТМ: Найти референсную сущность для выравнивания буллита
   *
   * Алгоритм:
   * 1. Идем вверх по цепочке сущностей
   * 2. Ищем первую подходящую сущность:
   *    - Bullet того же уровня (bullet-level-N)
   *    - ИЛИ любой heading
   *    - ИЛИ bullet меньшего уровня (опционально)
   * 3. Левая граница МАРКЕРА текущего буллита = левая граница ТЕКСТА найденной сущности
   *
   * @param entity - текущая сущность (bullet)
   * @param entities - все сущности метабулита
   * @returns референсная сущность или null (если не найдена - используем baseX)
   */
  private findReferenceEntityForBullet(
    entity: TextEntity,
    entities: TextEntity[],
  ): TextEntity | null {
    if (!isBulletType(entity.entityType)) {
      return null;
    }

    const currentLevel = this.extractBulletLevel(entity.entityType);
    if (currentLevel === null) {
      return null;
    }

    const currentIndex = entities.indexOf(entity);
    if (currentIndex <= 0) {
      return null; // Нет вышестоящих сущностей
    }

    // Идем вверх по цепочке
    for (let i = currentIndex - 1; i >= 0; i--) {
      const candidate = entities[i];

      // 1. Любой heading подходит
      if (candidate.entityType.startsWith('heading')) {
        return candidate;
      }

      // 2. Bullet того же уровня подходит
      if (isBulletType(candidate.entityType)) {
        const candidateLevel = this.extractBulletLevel(candidate.entityType);
        if (candidateLevel === currentLevel) {
          return candidate;
        }

        // 3. Bullet меньшего уровня тоже подходит
        // (например, b1 может ориентироваться на b1 выше, если нет heading)
        if (candidateLevel !== null && candidateLevel < currentLevel) {
          return candidate;
        }
      }
    }

    return null; // Не нашли референс - используем baseX
  }

  /**
   * ✅ ИСПРАВЛЕНО: Получить позицию для выравнивания с учетом типа референса
   *
   * Правила:
   * - Heading → возвращает позицию ТЕКСТА
   * - Bullet того же уровня → возвращает позицию МАРКЕРА (выравнивание на одной линии)
   * - Bullet меньшего уровня → возвращает позицию ТЕКСТА
   *
   * @param currentEntity - текущая сущность
   * @param referenceEntity - референсная сущность
   * @param targetPositions - уже рассчитанные позиции
   * @returns левая граница для выравнивания маркера или null
   */
  private getReferenceAlignmentPosition(
    currentEntity: TextEntity,
    referenceEntity: TextEntity,
    targetPositions: Array<{ entity: TextEntity; targetX: number; targetY: number }>,
  ): number | null {
    // Находим рассчитанную позицию референсной сущности
    const referencePosition = targetPositions.find((tp) => tp.entity.id === referenceEntity.id);
    if (!referencePosition) {
      return null;
    }

    // Определяем тип референса
    const isReferenceHeading = referenceEntity.entityType.startsWith('heading');
    const isReferenceBullet = isBulletType(referenceEntity.entityType);

    // Для heading всегда возвращаем позицию текста
    if (isReferenceHeading) {
      return referencePosition.targetX; // Позиция ТЕКСТА heading
    }

    // Для bullet проверяем уровень
    if (isReferenceBullet) {
      const currentLevel = this.extractBulletLevel(currentEntity.entityType);
      const referenceLevel = this.extractBulletLevel(referenceEntity.entityType);

      // Bullet того же уровня → возвращаем позицию МАРКЕРА (выравниваем маркеры на одной линии)
      if (currentLevel === referenceLevel) {
        // КРИТИЧНО: НЕ используем getEntityMarkerInfo - он берет СТАРУЮ позицию с канваса!
        // Вместо этого вычисляем где БУДЕТ маркер после применения targetPositions

        const refMarkerInfo = this.getEntityMarkerInfo(referenceEntity);
        if (refMarkerInfo.hasMarker) {
          const refMarkerWidth = refMarkerInfo.markerWidth || 0;
          const refMarkerSpacing = this.getMarkerSpacing(referenceEntity);

          // targetX референса - это позиция его ТЕКСТА (новая рассчитанная)
          // Маркер находится слева: markerLeft = textLeft - markerWidth - spacing
          const calculatedMarkerLeft =
            referencePosition.targetX - refMarkerWidth - refMarkerSpacing;
          return calculatedMarkerLeft; // Позиция МАРКЕРА референса (рассчитанная!)
        }
        // Если маркера нет у референса - возвращаем позицию текста
        return referencePosition.targetX;
      }

      // Bullet меньшего уровня → возвращаем позицию ТЕКСТА
      return referencePosition.targetX;
    }

    // Fallback: возвращаем позицию текста
    return referencePosition.targetX;
  }

  /**
   * Рассчитать позицию текста на основе заданного верхнего края маркера
   *
   * Этот метод решает обратную задачу: зная желаемый верхний край маркера,
   * вычисляет где должен быть расположен текст, чтобы маркер оказался на этой позиции.
   *
   * Используется когда spacingTop применяется к верхнему краю маркера, а не к тексту
   * (например, когда маркер выше текста из-за offsetY: 'center').
   *
   * @param entity - текстовая сущность
   * @param markerTopEdge - желаемый верхний край маркера
   * @returns позиция верха текста и минимальный верхний край
   */
  private getEntityTopFromMarkerTop(
    entity: TextEntity,
    markerTopEdge: number,
  ): { entityTop: number; markerMinTop: number } {
    const entityHeight = entity.height || 0;

    // Получаем конфиг маркера
    const stylesConfig = entity.getStylesConfig?.();
    const entityStyle = stylesConfig?.[entity.entityType];
    const markerConfig = entityStyle && 'marker' in entityStyle ? entityStyle.marker : undefined;

    // Проверяем есть ли РЕАЛЬНЫЙ маркер на канвасе
    const hasRealMarker = entity.hasMarker?.() || !!entity.markerId;

    if (!markerConfig || !markerConfig.enabled || !hasRealMarker) {
      return { entityTop: markerTopEdge, markerMinTop: markerTopEdge };
    }

    const markerHeight = markerConfig.position.height || 0;
    const offsetY = markerConfig.position.offsetY;

    // Рассчитываем позицию текста на основе верхнего края маркера
    // Используем обратную формулу для каждого типа offsetY
    let entityTop: number;
    if (typeof offsetY === 'number') {
      // markerTop = entityTop + offsetY => entityTop = markerTop - offsetY
      entityTop = markerTopEdge - offsetY;
    } else if (offsetY === 'top') {
      // markerTop = entityTop => entityTop = markerTop
      entityTop = markerTopEdge;
    } else if (offsetY === 'center') {
      // markerTop = entityTop + (entityHeight - markerHeight) / 2
      // => entityTop = markerTop - (entityHeight - markerHeight) / 2
      entityTop = markerTopEdge - (entityHeight - markerHeight) / 2;
    } else if (offsetY === 'bottom') {
      // markerTop = entityTop + entityHeight - markerHeight
      // => entityTop = markerTop - entityHeight + markerHeight
      entityTop = markerTopEdge - entityHeight + markerHeight;
    } else if (offsetY === 'centered-by-first-line') {
      // markerTop = entityTop + (firstLineHeight - markerHeight) / 2
      // => entityTop = markerTop - (firstLineHeight - markerHeight) / 2
      const fontSize = entity.fontSize || 16;
      const lineHeight = entity.lineHeight || 1.16;
      const firstLineHeight = fontSize * lineHeight;
      entityTop = markerTopEdge - (firstLineHeight - markerHeight) / 2;
    } else {
      entityTop = markerTopEdge;
    }

    const markerMinTop = Math.min(entityTop, markerTopEdge);
    return { entityTop, markerMinTop };
  }

  /**
   * Получить минимальный верхний край (маркера или текста) для заданной позиции текста
   *
   * Этот метод вычисляет где будет верхний край маркера, если текст расположен
   * на заданной позиции. Используется для определения, выходит ли маркер за границы текста сверху.
   *
   * @param entity - текстовая сущность
   * @param entityTop - позиция верха текста
   * @returns минимальный верхний край (если маркер выше текста - вернет верх маркера, иначе верх текста)
   */
  private getMarkerMinTop(entity: TextEntity, entityTop: number): { minTop: number } {
    const entityHeight = entity.height || 0;

    // Получаем конфиг маркера
    const stylesConfig = entity.getStylesConfig?.();
    const entityStyle = stylesConfig?.[entity.entityType];
    const markerConfig = entityStyle && 'marker' in entityStyle ? entityStyle.marker : undefined;

    // Проверяем есть ли РЕАЛЬНЫЙ маркер на канвасе
    const hasRealMarker = entity.hasMarker?.() || !!entity.markerId;

    if (!markerConfig || !markerConfig.enabled || !hasRealMarker) {
      return { minTop: entityTop };
    }

    const markerHeight = markerConfig.position.height || 0;
    const offsetY = markerConfig.position.offsetY;

    // Рассчитываем верхний край маркера в зависимости от offsetY
    let markerTopEdge: number;
    if (typeof offsetY === 'number') {
      markerTopEdge = entityTop + offsetY;
    } else if (offsetY === 'top') {
      markerTopEdge = entityTop;
    } else if (offsetY === 'center') {
      // Маркер центрируется относительно текста
      markerTopEdge = entityTop + (entityHeight - markerHeight) / 2;
    } else if (offsetY === 'bottom') {
      markerTopEdge = entityTop + entityHeight - markerHeight;
    } else if (offsetY === 'centered-by-first-line') {
      // Выравнивание по центру ПЕРВОЙ строки
      const fontSize = entity.fontSize || 16;
      const lineHeight = entity.lineHeight || 1.16;
      const firstLineHeight = fontSize * lineHeight;
      markerTopEdge = entityTop + (firstLineHeight - markerHeight) / 2;
    } else {
      markerTopEdge = entityTop;
    }

    // Возвращаем минимальный верхний край (либо текст, либо маркер)
    const minTop = Math.min(entityTop, markerTopEdge);
    return { minTop };
  }

  /**
   * Получить позиционную высоту сущности.
   * Для однострочного текста: fontSize * lineHeight из stylesConfig (консистентно с бэкендом).
   * Для многострочного текста (с переносами): entity.height из Fabric.js (реальная высота).
   * Это решает проблему расхождения позиций с бэкендом для однострочных сущностей,
   * при этом корректно обрабатывая многострочный текст с переносами.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getPositioningHeight(entity: TextEntity, entityStyle: any): number {
    // Если текст переносится на несколько строк, используем реальную высоту из Fabric.js
    const textLinesCount = entity.textLines?.length ?? 1;
    if (textLinesCount > 1) {
      return entity.height || 0;
    }

    // Однострочный текст: используем fontSize * lineHeight из stylesConfig
    if (entity.entityType === 'digital-accent' && entityStyle && 'digit' in entityStyle) {
      const digitConfig = entityStyle.digit;
      const fontSize = digitConfig.fontSize || entity.fontSize || 16;
      const lineHeight = digitConfig.lineHeight || 1.2;
      return fontSize * lineHeight;
    }

    if (entityStyle && 'fontSize' in entityStyle) {
      const fontSize = entityStyle.fontSize || entity.fontSize || 16;
      const lineHeight = entityStyle.lineHeight || 1.2;
      return fontSize * lineHeight;
    }

    // Fallback на Fabric.js высоту если stylesConfig недоступен
    return entity.height || 0;
  }

  /**
   * Получить эффективную высоту сущности с учетом маркера
   *
   * Эффективная высота - это вертикальное пространство, которое занимает сущность,
   * включая как сам текст, так и связанный с ним маркер (если он выходит за границы текста).
   *
   * Алгоритм:
   * 1. Рассчитываем верхний и нижний край текста
   * 2. Рассчитываем верхний и нижний край маркера (если есть)
   * 3. Берем минимальный верхний край и максимальный нижний край
   * 4. Добавляем spacingBottom (только если это НЕ последняя сущность)
   *
   * @param entity - текстовая сущность
   * @param targetTop - опциональная целевая позиция текста (используется при расчете позиций)
   * @param isLastEntity - флаг что это последняя сущность (не добавляем spacingBottom)
   * @returns объект с effectiveHeight (эффективная высота) и minTop (минимальный верхний край)
   */
  private getEffectiveEntityHeight(
    entity: TextEntity,
    targetTop?: number,
    isLastEntity: boolean = false,
  ): { effectiveHeight: number; minTop: number } {
    // Используем коэффициенты отступов (только если это НЕ последняя сущность)
    const spacingBottom = isLastEntity ? 0 : this.calculateSpacing(entity, 'bottom');

    // Границы текста - используем targetTop если передан, иначе текущую позицию
    const entityTop = targetTop !== undefined ? targetTop : entity.top || 0;
    // Fabric.js высота - используем для визуального позиционирования маркеров
    const entityHeight = entity.height || 0;

    // Получаем конфиг для расчета позиционной высоты и маркеров
    const stylesConfig = entity.getStylesConfig?.();
    const entityStyle = stylesConfig?.[entity.entityType];

    // Позиционная высота: для однострочного текста - fontSize * lineHeight (консистентно с бэкендом),
    // для многострочного - entity.height (Fabric.js, реальная высота с учетом переносов)
    const positioningHeight = this.getPositioningHeight(entity, entityStyle);
    const positioningBottom = entityTop + positioningHeight;

    const markerConfig = entityStyle && 'marker' in entityStyle ? entityStyle.marker : undefined;

    // Проверяем есть ли РЕАЛЬНЫЙ маркер на канвасе, а не только конфигурация
    // Это важно: если маркера нет визуально, не учитываем его размер в расчёте
    const hasRealMarker = entity.hasMarker?.() || !!entity.markerId;

    if (!markerConfig || !markerConfig.enabled || !hasRealMarker) {
      return {
        effectiveHeight: positioningHeight + spacingBottom,
        minTop: entityTop,
      };
    }

    const markerHeight = markerConfig.position.height || 0;
    const offsetY = markerConfig.position.offsetY;

    // Рассчитываем верхний край маркера в зависимости от offsetY
    let markerTopEdge: number;
    if (typeof offsetY === 'number') {
      markerTopEdge = entityTop + offsetY;
    } else if (offsetY === 'top') {
      markerTopEdge = entityTop;
    } else if (offsetY === 'center') {
      // Маркер центрируется относительно текста
      markerTopEdge = entityTop + (entityHeight - markerHeight) / 2;
    } else if (offsetY === 'bottom') {
      // Верхняя граница маркера = нижняя граница текста минус высота маркера
      markerTopEdge = entityTop + entityHeight - markerHeight;
    } else if (offsetY === 'centered-by-first-line') {
      // Выравнивание по центру ПЕРВОЙ строки
      const fontSize = entity.fontSize || 16;
      const lineHeight = entity.lineHeight || 1.16;
      const firstLineHeight = fontSize * lineHeight;
      markerTopEdge = entityTop + (firstLineHeight - markerHeight) / 2;
    } else {
      markerTopEdge = entityTop;
    }

    const markerBottomEdge = markerTopEdge + markerHeight;

    // Рассчитываем эффективные границы: берем минимальный верхний край и максимальный нижний край
    // Для нижней границы используем positioningBottom (консистентно с бэкендом)
    const minTop = Math.min(entityTop, markerTopEdge);
    const maxBottom = Math.max(positioningBottom, markerBottomEdge);

    // Эффективная высота = разница между максимальным нижним и минимальным верхним краем + spacingBottom
    const effectiveHeight = maxBottom - minTop + spacingBottom;

    return { effectiveHeight, minTop };
  }

  /**
   * Получить информацию о метабуллите для отладки
   */
  getDebugInfo(): string {
    const entities = this.getEntities();
    const markers = this.getMarkers();
    return `MetaBullet[${this.id}] entities: ${entities.length}, markers: ${markers.length}, ids: [${this.entityIds.join(', ')}]`;
  }
}
